/**
 * Pure field patches for a twin-test gate questions artifact.
 *
 * All edits go through `twinTestGateQuestionAdapter` so parse / serialize /
 * type-switch memory stay one contract. Mirrors ConfirmRunCard's commit-on-blur
 * model: each helper takes the staged body, mutates one field, and returns a
 * new body in the same fence wrapper.
 *
 * Cache scope: one-shot, not cached (local JSON surgery, no LLM).
 */

import {
  WorkflowQuickStudyStepConfig,
  WorkflowTemplate,
  optionPlaceholderLabel,
  remapTwinTestConditionNames,
  twinTestConditionQuestionIndexes,
  twinTestConditionReadsChoice,
  twinTestConditionReadsRow,
  twinTestFollowUpParentIndexes,
} from '@digital-twins/shared-types'

import {
  applyGateQuestionTypeSwitch,
  blankGateEditableQuestion,
  GateEditableColumn,
  GateEditableQuestion,
  GateEditableRow,
  GateTypeMemory,
  GateTypeStash,
  readTwinTestGateTypedBody,
  twinTestGateQuestionAdapter,
  migrateSharedScaleAnchorsToColumns,
  TWIN_TEST_GATE_DEFAULT_RATING_SPAN,
  TWIN_TEST_GATE_QUESTION_TYPES,
  typeNeedsColumns,
} from './twinTestGateAdapter'

/**
 * Fallback peer-question ceiling (D5, platform-wide cap of 2) for a run whose
 * template did not declare one, or declared none at all. Mirrors backend
 * `TWIN_TEST_MAX_QUESTIONS`. Prefer `twinTestGateMaxQuestions`, which reads
 * the declared max off the run's own template instead of this mirror.
 */
export const TWIN_TEST_MAX_QUESTIONS = 2

/**
 * The peer-question ceiling this gate should enforce: the template's own
 * `quick_study` step `maxQuestions`, not a hand-kept mirror of the backend
 * constant. Falls back to `TWIN_TEST_MAX_QUESTIONS` when the template is not
 * loaded yet or declares no `quick_study` step. Same shape as
 * `audienceInputKey` in `composeRunStudyDoc.ts`: one small pure reader so the
 * gate and any other caller read the same declaration.
 */
export function twinTestGateMaxQuestions(
  template: WorkflowTemplate | null | undefined,
): number {
  const quickStudyStep = template?.steps.find(
    (step): step is WorkflowQuickStudyStepConfig => step.kind === 'quick_study',
  )
  return quickStudyStep?.maxQuestions ?? TWIN_TEST_MAX_QUESTIONS
}

export {
  migrateSharedScaleAnchorsToColumns,
  TWIN_TEST_GATE_QUESTION_TYPES,
  twinTestGateQuestionAdapter,
  typeNeedsColumns,
}
export type {
  GateEditableColumn,
  GateEditableQuestion,
  GateEditableRow,
  GateTypeMemory,
  GateTypeStash,
}

/**
 * Why a gate edit left the body unchanged: a later question's `visibleIf`
 * reads what the edit would reshape (`dependency`), or the edit itself is not
 * a valid change (`invalid`: out of bounds, an empty span, a no-op). Only a
 * dependency refusal is explained to the reviewer.
 */
export type GateEditRefusal = 'dependency' | 'invalid'

export interface GateEditOutcome {
  body: string
  refusal: GateEditRefusal | null
}

function editQuestions(
  body: string,
  mutate: (
    questions: Array<GateEditableQuestion>,
  ) => Array<GateEditableQuestion> | GateEditRefusal,
): GateEditOutcome {
  if (!readTwinTestGateTypedBody(body)) {
    return { body, refusal: 'invalid' }
  }
  const questions = twinTestGateQuestionAdapter.parse(body)
  if (!questions) {
    return { body, refusal: 'invalid' }
  }
  const next = mutate(questions)
  if (typeof next === 'string') {
    return { body, refusal: next }
  }
  return { body: twinTestGateQuestionAdapter.serialize(next, body), refusal: null }
}

function withQuestions(
  body: string,
  mutate: (questions: Array<GateEditableQuestion>) => Array<GateEditableQuestion> | null,
): string {
  return editQuestions(body, (questions) => mutate(questions) ?? 'invalid').body
}

/** Peer question count (no follow-up / asked-unit accounting). */
export function countGateQuestions(body: string): number {
  const typed = readTwinTestGateTypedBody(body)
  return typed ? typed.entries.length : 0
}

/**
 * Questions that take a per-test slot: every entry except a conditional
 * follow-up (a runnable `visibleIf` that reads only earlier questions), which
 * runs on a subset and takes none, matching Nest's writer-side count.
 */
function countSlotQuestions(questions: Array<GateEditableQuestion>): number {
  return questions.filter(
    (question, index) =>
      !question.visibleIf || twinTestFollowUpParentIndexes(question.visibleIf, index) === null,
  ).length
}

/**
 * Cap at `maxQuestions` slot-taking questions (default:
 * `TWIN_TEST_MAX_QUESTIONS`). Conditional follow-ups do not count.
 */
export function canAddGateQuestion(
  body: string,
  maxQuestions: number = TWIN_TEST_MAX_QUESTIONS,
): boolean {
  if (!readTwinTestGateTypedBody(body)) {
    return false
  }
  const questions = twinTestGateQuestionAdapter.parse(body)
  return questions !== null && countSlotQuestions(questions) < maxQuestions
}

/**
 * Append a blank question of `questionType` (default `open_end`). Unchanged
 * body when at or over `maxQuestions` (default: `TWIN_TEST_MAX_QUESTIONS`).
 * Works from a valid empty list.
 */
export function addGateQuestion(
  body: string,
  maxQuestions: number = TWIN_TEST_MAX_QUESTIONS,
  questionType: string = 'open_end',
  language?: string | null,
): string {
  return withQuestions(body, (questions) => {
    if (countSlotQuestions(questions) >= maxQuestions) {
      return null
    }
    return [...questions, blankGateEditableQuestion(questionType, language)]
  })
}

/**
 * The 0-based positions of the questions whose `visibleIf` reads the question
 * at `questionIndex`. Conditions name questions by position, so removing or
 * reshaping a question one of these reads would point them at nothing, or at
 * the wrong question.
 */
export function gateQuestionDependents(
  body: string,
  questionIndex: number,
): Array<number> {
  const questions = twinTestGateQuestionAdapter.parse(body) ?? []
  return dependentIndexes(questions, (visibleIf) =>
    (twinTestConditionQuestionIndexes(visibleIf) ?? []).includes(questionIndex),
  )
}

/**
 * The 0-based positions of the questions whose `visibleIf` compares the
 * question at `questionIndex` against its choice `choiceIndex` by text.
 * Renaming or removing that choice would make the condition match nobody.
 */
export function gateChoiceDependents(
  body: string,
  questionIndex: number,
  choiceIndex: number,
): Array<number> {
  const questions = twinTestGateQuestionAdapter.parse(body) ?? []
  const choice = questions[questionIndex]?.choices[choiceIndex]
  if (choice === undefined) {
    return []
  }
  return dependentIndexes(questions, (visibleIf) =>
    twinTestConditionReadsChoice(visibleIf, questionIndex, choice),
  )
}

/**
 * The 0-based positions of the questions whose `visibleIf` reads row
 * `rowIndex` of the grid question at `questionIndex`: by its id when the
 * condition names the row (`{q1.rb}`), or any read of the whole grid, which
 * cannot be pinned to one row. Removing such a row would make the condition
 * read an answer nobody can give.
 */
export function gateRowDependents(
  body: string,
  questionIndex: number,
  rowIndex: number,
): Array<number> {
  const questions = twinTestGateQuestionAdapter.parse(body) ?? []
  const row = questions[questionIndex]?.rows?.[rowIndex]
  if (!row) {
    return []
  }
  return dependentIndexes(questions, (visibleIf) =>
    twinTestConditionReadsRow(visibleIf, questionIndex, row.id),
  )
}

/**
 * The 0-based positions of the questions whose `visibleIf` quotes column
 * `columnIndex` of the grid question at `questionIndex` by its label, as a
 * categorical grid answer is compared. Renaming it would make the condition
 * match nobody.
 */
export function gateColumnLabelDependents(
  body: string,
  questionIndex: number,
  columnIndex: number,
): Array<number> {
  const questions = twinTestGateQuestionAdapter.parse(body) ?? []
  const column = questions[questionIndex]?.columns?.[columnIndex]
  if (!column) {
    return []
  }
  return dependentIndexes(questions, (visibleIf) =>
    twinTestConditionReadsChoice(visibleIf, questionIndex, column.label),
  )
}

/**
 * Whether moving a rating span from `from` to `to` drops a value the old span
 * offered. A dependent condition may compare against any of those values, so
 * a narrowing is refused while one exists; a widening keeps every value and
 * always passes. A span with no usable bounds yet cannot be narrowed.
 */
export function isGateSpanNarrowing(
  from: { min: number | null; max: number | null },
  to: { min: number; max: number },
): boolean {
  if (
    from.min === null ||
    from.max === null ||
    !Number.isFinite(from.min) ||
    !Number.isFinite(from.max)
  ) {
    return false
  }
  return to.min > from.min || to.max < from.max
}

function dependentIndexes(
  questions: Array<GateEditableQuestion>,
  reads: (visibleIf: string) => boolean,
): Array<number> {
  const dependents: Array<number> = []
  questions.forEach((question, index) => {
    if (question.visibleIf && reads(question.visibleIf)) {
      dependents.push(index)
    }
  })
  return dependents
}

/**
 * Drop one peer question. Empty list is allowed (Approve stays disabled until
 * the reviewer adds at least one back).
 *
 * Refused (body unchanged) while a later question's `visibleIf` reads this
 * one: the reviewer removes the dependent question first. Every other later
 * condition is renamed by question identity (the same
 * `remapTwinTestConditionNames` the writer-side check runs), so `{q3}` still
 * reads the question it read before Q1 left.
 */
export function removeGateQuestion(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    if (questionIndex < 0 || questionIndex >= questions.length) {
      return null
    }
    const survivorIndexByOldIndex = questions.map((_question, index) =>
      index === questionIndex ? null : index < questionIndex ? index : index - 1,
    )
    const next: Array<GateEditableQuestion> = []
    for (let index = 0; index < questions.length; index += 1) {
      if (index === questionIndex) {
        continue
      }
      const question = questions[index]
      if (!question.visibleIf) {
        next.push(question)
        continue
      }
      const remapped = remapTwinTestConditionNames(
        question.visibleIf,
        survivorIndexByOldIndex,
      )
      if (remapped.droppedIndexes.length > 0) {
        return null
      }
      next.push({ ...question, visibleIf: remapped.visibleIf })
    }
    return next
  })
}

export function setGateQuestionPrompt(
  body: string,
  questionIndex: number,
  prompt: string,
): string {
  const next = prompt.trim()
  if (!next) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, prompt: next }
    return nextQuestions
  })
}

export function setGateQuestionTitle(
  body: string,
  questionIndex: number,
  title: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, title: title.trim() }
    return nextQuestions
  })
}

export function setGateQuestionChoice(
  body: string,
  questionIndex: number,
  choiceIndex: number,
  value: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    if (choiceIndex < 0 || choiceIndex >= question.choices.length) {
      return null
    }
    const next = value.trim()
    if (!next) {
      return null
    }
    if (
      next !== question.choices[choiceIndex] &&
      gateChoiceDependents(body, questionIndex, choiceIndex).length > 0
    ) {
      return null
    }
    const choices = [...question.choices]
    choices[choiceIndex] = next
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, choices }
    return nextQuestions
  })
}

export function addGateQuestionChoice(
  body: string,
  questionIndex: number,
  language?: string | null,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    const nextLabel = optionPlaceholderLabel(
      language ?? undefined,
      question.choices.length + 1,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      choices: [...question.choices, nextLabel],
    }
    return nextQuestions
  })
}

/**
 * Remove a choice; refuses when fewer than 2 would remain, or while a later
 * question's `visibleIf` compares against it (`gateChoiceDependents`).
 */
export function removeGateQuestionChoice(
  body: string,
  questionIndex: number,
  choiceIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return null
    }
    if (question.choices.length <= 2) {
      return null
    }
    if (choiceIndex < 0 || choiceIndex >= question.choices.length) {
      return null
    }
    if (gateChoiceDependents(body, questionIndex, choiceIndex).length > 0) {
      return null
    }
    const choices = question.choices.filter(
      (_choice, index) => index !== choiceIndex,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, choices }
    return nextQuestions
  })
}

export function setGateQuestionScaleLabel(
  body: string,
  questionIndex: number,
  which: 'minLabel' | 'maxLabel',
  label: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.scale) {
      return null
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      scale: {
        ...question.scale,
        [which]: label.trim(),
      },
    }
    return nextQuestions
  })
}

export function setGateQuestionScaleSpan(
  body: string,
  questionIndex: number,
  min: number,
  max: number,
): string {
  return setGateQuestionScaleSpanOutcome(body, questionIndex, min, max).body
}

/** `setGateQuestionScaleSpan` with the reason a refused span stays as set. */
export function setGateQuestionScaleSpanOutcome(
  body: string,
  questionIndex: number,
  min: number,
  max: number,
): GateEditOutcome {
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.scale) {
      return 'invalid'
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return 'invalid'
    }
    if (
      isGateSpanNarrowing(question.scale, { min, max }) &&
      gateQuestionDependents(body, questionIndex).length > 0
    ) {
      return 'dependency'
    }
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = {
      ...question,
      scale: {
        ...question.scale,
        min,
        max,
      },
    }
    return nextQuestions
  })
}

/**
 * Change question type. `memory` is mutated (studies-like stash per prior type).
 * Returns the new body; memory map is updated in place by the caller-owned Map.
 *
 * Refused (body unchanged) while a later question's `visibleIf` reads this
 * one: a new type answers in a new shape, so the condition would match nobody.
 */
export function setGateQuestionType(
  body: string,
  questionIndex: number,
  nextType: string,
  memory: GateTypeMemory,
  language?: string | null,
): string {
  return setGateQuestionTypeOutcome(body, questionIndex, nextType, memory, language).body
}

/** `setGateQuestionType` with the reason a refused type stays as set. */
export function setGateQuestionTypeOutcome(
  body: string,
  questionIndex: number,
  nextType: string,
  memory: GateTypeMemory,
  language?: string | null,
): GateEditOutcome {
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question) {
      return 'invalid'
    }
    if (question.questionType === nextType) {
      return 'invalid'
    }
    if (!TWIN_TEST_GATE_QUESTION_TYPES.includes(nextType)) {
      return 'invalid'
    }
    if (gateQuestionDependents(body, questionIndex).length > 0) {
      return 'dependency'
    }

    const result = applyGateQuestionTypeSwitch(
      question,
      nextType,
      memory.get(questionIndex),
      language,
    )
    memory.set(questionIndex, result.memory)

    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = result.question
    return nextQuestions
  })
}

/** Default rating span for blank rating cards (shared with adapter). */
export { TWIN_TEST_GATE_DEFAULT_RATING_SPAN }

function columnId(index: number): string {
  return index < 26 ? `c${String.fromCharCode(97 + index)}` : `col_${index + 1}`
}

function rowId(index: number): string {
  return index < 26 ? `r${String.fromCharCode(97 + index)}` : `row_${index + 1}`
}

export function setGateQuestionRowLabel(
  body: string,
  questionIndex: number,
  rowIndex: number,
  label: string,
): string {
  const next = label.trim()
  if (!next) {
    return body
  }
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.rows || rowIndex < 0 || rowIndex >= question.rows.length) {
      return null
    }
    const rows = question.rows.map((row, index) =>
      index === rowIndex ? { ...row, label: next } : row,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

export function addGateQuestionRow(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question || !typeNeedsColumns(question.questionType)) {
      return null
    }
    const existing = question.rows ?? []
    const rows: Array<GateEditableRow> = [
      ...existing,
      {
        id: rowId(existing.length),
        label: `Row ${existing.length + 1}`,
      },
    ]
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

/**
 * Remove a row; refuses when fewer than 1 would remain, or while a later
 * question's `visibleIf` reads it (`gateRowDependents`).
 */
export function removeGateQuestionRow(
  body: string,
  questionIndex: number,
  rowIndex: number,
): string {
  return removeGateQuestionRowOutcome(body, questionIndex, rowIndex).body
}

/** `removeGateQuestionRow` with the reason a refused row stays. */
export function removeGateQuestionRowOutcome(
  body: string,
  questionIndex: number,
  rowIndex: number,
): GateEditOutcome {
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.rows || question.rows.length <= 1) {
      return 'invalid'
    }
    if (rowIndex < 0 || rowIndex >= question.rows.length) {
      return 'invalid'
    }
    if (gateRowDependents(body, questionIndex, rowIndex).length > 0) {
      return 'dependency'
    }
    const rows = question.rows.filter((_row, index) => index !== rowIndex)
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, rows }
    return nextQuestions
  })
}

export function setGateQuestionColumnLabel(
  body: string,
  questionIndex: number,
  columnIndex: number,
  label: string,
): string {
  return setGateQuestionColumnLabelOutcome(body, questionIndex, columnIndex, label).body
}

/** `setGateQuestionColumnLabel` with the reason a refused label stays as set. */
export function setGateQuestionColumnLabelOutcome(
  body: string,
  questionIndex: number,
  columnIndex: number,
  label: string,
): GateEditOutcome {
  const next = label.trim()
  if (!next) {
    return { body, refusal: 'invalid' }
  }
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return 'invalid'
    }
    if (
      next !== question.columns[columnIndex].label &&
      gateColumnLabelDependents(body, questionIndex, columnIndex).length > 0
    ) {
      return 'dependency'
    }
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, label: next } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns }
    return nextQuestions
  })
}

export function setGateQuestionColumnScaleLabel(
  body: string,
  questionIndex: number,
  columnIndex: number,
  end: 'min' | 'max',
  value: string,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return null
    }
    const trimmed = value.trim()
    const stored = trimmed.length > 0 ? trimmed : null
    const key = end === 'min' ? 'minLabel' : 'maxLabel'
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, [key]: stored } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns }
    return nextQuestions
  })
}

export function setGateQuestionColumnSpan(
  body: string,
  questionIndex: number,
  columnIndex: number,
  min: number,
  max: number,
): string {
  return setGateQuestionColumnSpanOutcome(body, questionIndex, columnIndex, min, max).body
}

/** `setGateQuestionColumnSpan` with the reason a refused span stays as set. */
export function setGateQuestionColumnSpanOutcome(
  body: string,
  questionIndex: number,
  columnIndex: number,
  min: number,
  max: number,
): GateEditOutcome {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return { body, refusal: 'invalid' }
  }
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (
      !question?.columns ||
      columnIndex < 0 ||
      columnIndex >= question.columns.length
    ) {
      return 'invalid'
    }
    // A condition can read any cell of the grid, so the whole question's
    // dependents guard every column's span (widening still passes).
    if (
      isGateSpanNarrowing(question.columns[columnIndex], { min, max }) &&
      gateQuestionDependents(body, questionIndex).length > 0
    ) {
      return 'dependency'
    }
    const columns = question.columns.map((column, index) =>
      index === columnIndex ? { ...column, min, max } : column,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = migrateSharedScaleAnchorsToColumns({
      ...question,
      columns,
    })
    return nextQuestions
  })
}

export function addGateQuestionColumn(
  body: string,
  questionIndex: number,
): string {
  return withQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question || !typeNeedsColumns(question.questionType)) {
      return null
    }
    const span = TWIN_TEST_GATE_DEFAULT_RATING_SPAN
    const isCategorical = question.questionType === 'matrix_categorical'
    const migrated = migrateSharedScaleAnchorsToColumns(question)
    const existing = migrated.columns ?? []
    const columns: Array<GateEditableColumn> = [
      ...existing,
      {
        id: columnId(existing.length),
        label: isCategorical
          ? `Category ${existing.length + 1}`
          : `Measure ${existing.length + 1}`,
        min: isCategorical ? null : span.min,
        max: isCategorical ? null : span.max,
        minLabel: null,
        maxLabel: null,
      },
    ]
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...migrated, columns }
    return nextQuestions
  })
}

/**
 * Remove a column; refuses when fewer than 1 (rating) or 2 (categorical)
 * remain, or while a later question's `visibleIf` reads this grid at all: a
 * condition can compare against any column's cells or categories, and which
 * one is not always visible in its names, so the whole question is locked.
 */
export function removeGateQuestionColumn(
  body: string,
  questionIndex: number,
  columnIndex: number,
): string {
  return removeGateQuestionColumnOutcome(body, questionIndex, columnIndex).body
}

/** `removeGateQuestionColumn` with the reason a refused column stays. */
export function removeGateQuestionColumnOutcome(
  body: string,
  questionIndex: number,
  columnIndex: number,
): GateEditOutcome {
  return editQuestions(body, (questions) => {
    const question = questions[questionIndex]
    if (!question?.columns) {
      return 'invalid'
    }
    const minColumns =
      question.questionType === 'matrix_categorical' ? 2 : 1
    if (question.columns.length <= minColumns) {
      return 'invalid'
    }
    if (columnIndex < 0 || columnIndex >= question.columns.length) {
      return 'invalid'
    }
    if (gateQuestionDependents(body, questionIndex).length > 0) {
      return 'dependency'
    }
    const columns = question.columns.filter(
      (_column, index) => index !== columnIndex,
    )
    const nextQuestions = [...questions]
    nextQuestions[questionIndex] = { ...question, columns, scale: null }
    return nextQuestions
  })
}
